import * as THREE from "three";

/** A colour gradient = ordered stops. `t` in 0..1, `color` a hex string. */
export type GradientStop = { t: number; color: string };
export type GradientAxis = "x" | "y" | "z" | "radial";

export const DEFAULT_GRADIENT: GradientStop[] = [
  { t: 0, color: "#ff2d95" },
  { t: 0.5, color: "#7a5cff" },
  { t: 1, color: "#35e0ff" },
];

export const GRADIENT_PRESETS: { name: string; stops: GradientStop[] }[] = [
  { name: "Rainbow", stops: [
    { t: 0, color: "#ff0040" }, { t: 0.2, color: "#ff9500" }, { t: 0.4, color: "#ffe500" },
    { t: 0.6, color: "#28e070" }, { t: 0.8, color: "#2a6cff" }, { t: 1, color: "#a34bff" },
  ] },
  { name: "Sunset", stops: [{ t: 0, color: "#ff5e62" }, { t: 0.5, color: "#ff9966" }, { t: 1, color: "#ffcf40" }] },
  { name: "Ocean", stops: [{ t: 0, color: "#0f3f6e" }, { t: 0.5, color: "#1e9fc8" }, { t: 1, color: "#5cffe0" }] },
  { name: "Fire", stops: [{ t: 0, color: "#3a0000" }, { t: 0.45, color: "#ff2200" }, { t: 0.75, color: "#ff9500" }, { t: 1, color: "#ffee88" }] },
  { name: "Ice", stops: [{ t: 0, color: "#123a7a" }, { t: 0.5, color: "#4fd0ff" }, { t: 1, color: "#eafcff" }] },
  { name: "Neon", stops: [{ t: 0, color: "#ff00e5" }, { t: 0.5, color: "#00ffd5" }, { t: 1, color: "#faff00" }] },
  { name: "Aurora", stops: [{ t: 0, color: "#00ffa3" }, { t: 0.5, color: "#3a7bff" }, { t: 1, color: "#c840ff" }] },
];

const _a = new THREE.Color();
const _b = new THREE.Color();

/** Sample a gradient at `t` (0..1) → linear-RGB triple 0..1. Pure. */
export function sampleGradient(stops: GradientStop[], t: number): [number, number, number] {
  if (stops.length === 0) return [1, 1, 1];
  const s = [...stops].sort((p, q) => p.t - q.t);
  if (t <= s[0].t) { _a.set(s[0].color); return [_a.r, _a.g, _a.b]; }
  const last = s[s.length - 1];
  if (t >= last.t) { _a.set(last.color); return [_a.r, _a.g, _a.b]; }
  for (let i = 0; i < s.length - 1; i++) {
    if (t >= s[i].t && t <= s[i + 1].t) {
      const span = s[i + 1].t - s[i].t || 1;
      const f = (t - s[i].t) / span;
      _a.set(s[i].color);
      _b.set(s[i + 1].color);
      return [_a.r + (_b.r - _a.r) * f, _a.g + (_b.g - _a.g) * f, _a.b + (_b.b - _a.b) * f];
    }
  }
  _a.set(last.color);
  return [_a.r, _a.g, _a.b];
}

/** Colour a point cloud by a gradient mapped along an axis (or radius). Pure. */
export function gradientColors(positions: Float32Array, stops: GradientStop[], axis: GradientAxis): Float32Array {
  const n = positions.length / 3;
  const out = new Float32Array(positions.length);
  const coord = new Float32Array(n);
  let lo = Infinity, hi = -Infinity;
  for (let i = 0; i < n; i++) {
    const x = positions[i * 3], y = positions[i * 3 + 1], z = positions[i * 3 + 2];
    const c = axis === "x" ? x : axis === "y" ? y : axis === "z" ? z : Math.hypot(x, y, z);
    coord[i] = c;
    if (c < lo) lo = c;
    if (c > hi) hi = c;
  }
  const range = hi - lo || 1;
  for (let i = 0; i < n; i++) {
    const [r, g, b] = sampleGradient(stops, (coord[i] - lo) / range);
    out[i * 3] = r;
    out[i * 3 + 1] = g;
    out[i * 3 + 2] = b;
  }
  return out;
}

/**
 * Recolour a cloud by a look (structurally matches the sequencer's SceneLook —
 * kept structural so gradient.ts stays dependency-free). "source" returns the
 * original colours untouched; solid/gradient return a fresh array. Pure.
 */
export function colorizeCloud(
  positions: Float32Array,
  colors: Float32Array,
  look: { color: "source" } | { color: "solid"; hex: string } | { color: "gradient"; stops: GradientStop[]; axis: GradientAxis }
): Float32Array {
  if (look.color === "source") return colors;
  if (look.color === "gradient") return gradientColors(positions, look.stops, look.axis);
  _a.set(look.hex);
  const out = new Float32Array(colors.length);
  for (let i = 0; i < out.length; i += 3) {
    out[i] = _a.r; out[i + 1] = _a.g; out[i + 2] = _a.b;
  }
  return out;
}

/** The gradient's colour at `t` as a hex string (for a new stop). */
export function hexAt(stops: GradientStop[], t: number): string {
  const [r, g, b] = sampleGradient(stops, t);
  return "#" + _a.setRGB(r, g, b).getHexString();
}

/** CSS `linear-gradient(...)` string for a preview swatch. */
export function gradientCss(stops: GradientStop[]): string {
  const s = [...stops].sort((p, q) => p.t - q.t);
  return `linear-gradient(to right, ${s.map((x) => `${x.color} ${(x.t * 100).toFixed(1)}%`).join(", ")})`;
}
